import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Container, SectionLabel, CampaignCard, TrustChip, CardGridSkeleton } from "@/components/shared";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import {
  ShieldCheck, Zap, Lock, ArrowRight, Flag, Users, HeartHandshake, Search, Receipt,
} from "lucide-react";
import { Brand } from "@/components/Brand";
import HeroCarousel from "@/components/HeroCarousel";
import hero1 from "@/assets/hero/hero-1.jpg";
import hero2 from "@/assets/hero/hero-2.jpg";
import hero3 from "@/assets/hero/hero-3.jpg";
import hero4 from "@/assets/hero/hero-4.jpg";

const HERO_IMAGES = [hero1, hero2, hero3, hero4];

export default function Home() {
  const [campaigns, setCampaigns] = useState(null);

  useEffect(() => {
    api.get("/campaigns", { params: { status: "live", limit: 6 } })
      .then((r) => setCampaigns(r.data))
      .catch(() => setCampaigns([]));
  }, []);

  return (
    <div>
      <Seo />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ff-border bg-white">
        <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-7 ff-fade-up">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-ff-border bg-[#F3F4F6] px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-ff-blue">
              <Flag className="h-3.5 w-3.5 text-ff-red" /> USA Based · Only available in the USA
            </div>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ff-blue sm:text-5xl lg:text-6xl">
              Helping Americans<br />
              Fight the <span className="text-ff-red">Good Fight.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ff-dark sm:text-lg">
              Donate in seconds. Start a fundraiser in minutes. <Brand /> is a U.S.-Based Crowdfunding
              Platform helping individuals, families, nonprofits, and communities raise support quickly —
              with campaign review, verified organizers, and payout protection built in from day one.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-ff-action text-base font-semibold text-white shadow-md hover:bg-ff-actionDark">
                <Link to="/browse" data-testid="hero-donate-now">
                  Donate Now <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-ff-action text-base font-semibold text-ff-action hover:bg-ff-action hover:text-white">
                <Link to="/start-a-fundraiser" data-testid="hero-start-fundraiser">Start a Fundraiser</Link>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <TrustChip label="Campaign Reviewed" />
              <TrustChip label="Organizer Verified" />
              <TrustChip label="Nonprofit Verified" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroCarousel images={HERO_IMAGES} />
          </div>
        </Container>
      </section>

      {/* VALUE PROPS */}
      <section className="border-b border-ff-border bg-[#F3F4F6]">
        <Container className="grid gap-6 py-16 md:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Zap, title: "Fast for donors", body: "Guest checkout in seconds. No account required. Email receipts on every donation." },
            { icon: ShieldCheck, title: "Strict for fundraisers", body: "Every campaign is reviewed before it can accept public donations. Verification scales with risk." },
            { icon: Lock, title: "Reviewed before Payout", body: "Payouts release only through Stripe rails after review readiness and settlement." },
            { icon: Receipt, title: "Transparent fees", body: "A flat 5% platform fee (3% for Pro) plus Stripe processing — clearly shown before you give. No hidden fees, ever." },
          ].map((v) => (
            <div key={v.title} className="rounded-xl border border-ff-border bg-white p-8 shadow-sm">
              <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-ff-blue">
                <v.icon className="h-6 w-6 text-white" />
              </span>
              <h3 className="font-heading text-xl font-bold text-ff-blue">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ff-dark">{v.body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* FEATURED CAMPAIGNS */}
      <section className="bg-white">
        <Container className="py-16">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <SectionLabel>Live now</SectionLabel>
              <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-ff-blue sm:text-3xl lg:text-4xl">
                Reviewed campaigns you can trust
              </h2>
            </div>
            <Button asChild variant="ghost" className="hidden text-ff-red hover:text-ff-red sm:inline-flex">
              <Link to="/browse" data-testid="home-view-all">View all <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
          {campaigns === null ? (
            <CardGridSkeleton count={3} />
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {campaigns.map((c) => <CampaignCard key={c.id} c={c} />)}
            </div>
          )}
        </Container>
      </section>

      {/* HOW IT WORKS STRIP */}
      <section className="border-y border-ff-border bg-ff-blue text-white">
        <Container className="grid gap-10 py-16 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionLabel><span className="text-white/60">How it works</span></SectionLabel>
            <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Built for U.S. fundraisers, donors, and nonprofits
            </h2>
            <p className="mt-4 max-w-lg text-white/70">
              Create a fundraiser for free. Complete organizer and payout onboarding. Our team reviews
              your campaign before it goes live and before funds are released.
            </p>
            <Button asChild className="mt-6 bg-white font-semibold text-ff-blue hover:bg-white/90">
              <Link to="/how-it-works" data-testid="home-how-it-works">See the full process</Link>
            </Button>
          </div>
          <div className="grid gap-4">
            {[
              { icon: Search, t: "1. Start & submit", d: "Build your campaign and submit for review." },
              { icon: ShieldCheck, t: "2. Reviewed & approved", d: "We verify before public donations open." },
              { icon: Users, t: "3. Raise & receive", d: "Donors give in seconds; payouts release under control." },
            ].map((s) => (
              <div key={s.t} className="flex items-start gap-4 rounded-xl bg-white/5 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ff-red">
                  <s.icon className="h-5 w-5 text-white" />
                </span>
                <div>
                  <div className="font-heading font-bold">{s.t}</div>
                  <div className="text-sm text-white/70">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* NONPROFIT CTA */}
      <section className="bg-white">
        <Container className="py-16">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-ff-border bg-[#F3F4F6] p-10 md:flex-row md:items-center">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-ff-green/10">
                <HeartHandshake className="h-6 w-6 text-ff-green" />
              </span>
              <div>
                <h3 className="font-heading text-xl font-bold text-ff-blue sm:text-2xl">Are you a U.S. nonprofit?</h3>
                <p className="mt-1 max-w-lg text-sm text-ff-dark">
                  Get verified to earn the Nonprofit Verified badge and unlock receipt support for your donors.
                </p>
              </div>
            </div>
            <Button asChild size="lg" className="bg-ff-blue font-semibold text-white hover:bg-ff-blue/90">
              <Link to="/for-nonprofits" data-testid="home-nonprofit-cta">Learn more</Link>
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}
